import React, { useState } from "react";
import { Heart, Leaf, Award, ShieldCheck } from "lucide-react";
import FloatingMacarons from "../components/FloatingMacarons";

const VALUES = [
   {
  icon: <ShieldCheck className="text-rose-400" size={28} />,
  title: "Food Safety Certified",
  desc: "Certified in food handler safety so every order meets the highest standards of care.",
  color: "bg-rose-50 border-rose-200",
  },  
  {
    icon: <Leaf className="text-sage-600" size={28} />,
    title: <>100%<br />Gluten Free</>,
    desc: "A dedicated gluten-free kitchen you can trust.",
    color: "bg-sage-50 border-sage-200",
  },
   {
    icon: <Award className="text-amber-500" size={28} />,
    title: "Quality Ingredients",
    desc: "Only the best ingredients - nothing artificial.",
    color: "bg-amber-50 border-amber-200",
  },
  {
    icon: <Heart className="text-red-400" size={28} />,
    title: <>Carefully<br />Crafted</>,
    desc: "Every batch is handmade, filled, and packaged with care.",
    color: "bg-cream-50 border-cream-200",
  },
 
 
];

// Hero macarons — 3 per side
const HERO_MACARONS = [
  { top: "15%", left: "2%",  delay: 0.0, size: 54 },
  { top: "50%", left: "8%",  delay: 1.2, size: 46 },
  { top: "80%", left: "2%",  delay: 2.4, size: 50 },
  { top: "15%", left: "92%", delay: 0.6, size: 50 },
  { top: "50%", left: "84%", delay: 1.8, size: 44 },
  { top: "80%", left: "91%", delay: 3.0, size: 52 },
];

// Content section macarons — cascading full length
const CONTENT_MACARONS = [
  { top: "3%",  left: "8%",  delay: 0.2, size: 44 },
  { top: "14%", left: "2%",  delay: 1.5, size: 34 },
  { top: "25%", left: "8%",  delay: 0.8, size: 50 },
  { top: "36%", left: "2%",  delay: 2.0, size: 36 },
  { top: "47%", left: "8%",  delay: 0.4, size: 44 },
  { top: "58%", left: "2%",  delay: 1.3, size: 34 },
  { top: "69%", left: "8%",  delay: 0.9, size: 48 },
  { top: "80%", left: "2%",  delay: 1.7, size: 36 },
  { top: "91%", left: "8%",  delay: 0.5, size: 42 },
  { top: "3%",  left: "84%", delay: 0.6, size: 40 },
  { top: "14%", left: "92%", delay: 1.9, size: 50 },
  { top: "25%", left: "84%", delay: 0.1, size: 34 },
  { top: "36%", left: "92%", delay: 2.3, size: 46 },
  { top: "47%", left: "84%", delay: 0.7, size: 36 },
  { top: "58%", left: "92%", delay: 1.4, size: 52 },
  { top: "69%", left: "84%", delay: 0.3, size: 34 },
  { top: "80%", left: "92%", delay: 2.1, size: 44 },
  { top: "91%", left: "84%", delay: 1.0, size: 38 },
];

function ValueCard({ icon, title, desc, color }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`card p-4 text-center border transition-all duration-300 ${color} ${hovered ? "shadow-md" : ""}`}
      style={{ transform: hovered ? "translateY(-4px)" : "translateY(0)" }}
    >
      <div className={`flex justify-center mb-3 transition-transform duration-300 ${hovered ? "scale-110" : ""}`}>
        {icon}
      </div>
      <h3 className="font-display text-xl text-sage-800 mb-2">{title}</h3>
      <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
    </div>
  );
}

export default function About() {
  return (
    <div className="pt-20">

      {/* Hero */}
      <section className="relative text-center py-16 px-4 bg-gradient-to-b from-cream-100 to-cream-50 overflow-hidden">
        <FloatingMacarons positions={HERO_MACARONS} heroOnly />
        <p className="font-handwrite text-sage-500 text-2xl mb-2">Our story</p>
        <h1 className="font-display text-4xl text-sage-900 mb-3">About Us</h1>
        <p className="text-gray-500 text-sm max-w-sm mx-auto">
          A dedication to gluten-free goodness right here in Aubrey, TX.
        </p>
      </section>

      {/* Story + Values — one continuous seamless section */}
      <section className="relative bg-cream-50 pt-0 pb-16 px-4 overflow-hidden">
        <FloatingMacarons positions={CONTENT_MACARONS} />
        <div className="relative max-w-2xl mx-auto space-y-10">

          {/* Story */}
          <div className="space-y-6 text-gray-600 leading-relaxed pt-4">
            <p>
            Once upon a time, in a kitchen near you, there was a <em>"how hard could it be"</em> attempt to make macarons. Let's just say that first batch was humbling…
            </p>
            <p>
            With time, and a bit of stubborn determination, what started as a humbling attempt became something people actually asked for. Family, then friends, then strangers. Turns out, a really good macaron has a way of finding its people.
            </p>
            <p>
            And so, Stewart's Sweet Shells came to be.
            </p>
          </div>
          <br/>
          {/* Soft divider */}
          <div className="flex items-center gap-4">
            <div className="flex-1 h-px bg-cream-200" />
            <div className="flex-1 h-px bg-cream-200" />
          </div>

          {/* Our Promise */}
          <div className="pt-4">
            <p className="font-handwrite text-sage-500 text-2xl text-center mb-4">Our values</p>
            <h1 className="font-display text-4xl text-sage-800 text-center mb-2">Our Promise</h1>
            <br/ >
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {VALUES.map((v) => <ValueCard key={v.title} {...v} />)}
            </div>
          </div>

        </div>
      </section>
    </div>
  );
}

